import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/** Landing page the backend's /sso/callback redirects to. Receives
 * either ``?token=<jwt>&return_to=<path>`` (happy path) or
 * ``?error=<message>`` (anything went wrong). Drops the token into
 * AuthContext and bounces to the requested return path, or shows the
 * error with a "Back to sign-in" link. */
export default function SsoReturn() {
  const { acceptSsoToken } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const token = params.get("token");
    const err = params.get("error");
    const returnTo = params.get("return_to") || "/";

    if (err) {
      setError(err);
      return;
    }
    if (!token) {
      setError("Sign-in response missing token");
      return;
    }
    let cancelled = false;
    acceptSsoToken(token)
      .then(() => {
        if (!cancelled) navigate(returnTo, { replace: true });
      })
      .catch(() => {
        if (!cancelled) setError("Token was rejected by the server");
      });
    return () => {
      cancelled = true;
    };
  }, [params, acceptSsoToken, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-gray-900 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-sm p-8 text-center">
        {!error ? (
          <>
            <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">Signing you in…</div>
            <div className="h-1.5 w-full bg-brand/20 overflow-hidden rounded">
              <div className="h-full w-1/3 bg-brand animate-pulse" />
            </div>
          </>
        ) : (
          <>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Sign-in failed</h1>
            <p className="text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg break-words">
              {error}
            </p>
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="mt-5 w-full bg-brand text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-brand-dark transition-colors"
            >
              Back to sign-in
            </button>
          </>
        )}
      </div>
    </div>
  );
}
